import React, {useEffect, useRef, useState} from 'react';

interface FilterPillProps {
  label: string;
  active?: boolean;
  valueSummary?: string;
  onApply: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

// LinkedIn-style filter pill: a rounded toggle button that opens a popover holding the
// filter's input(s), with Clear / Apply actions. Closes on outside click or after an action.
export const FilterPill: React.FC<FilterPillProps> = ({label, active = false, valueSummary, onApply, onClear, children}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`flex max-w-[260px] items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition ${
          active ? 'border-[#2678FA] bg-[#2678FA]/10 text-[#2678FA]' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}>
        <span className="truncate">{active && valueSummary ? `${label}: ${valueSummary}` : label}</span>
        <svg
          className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl">
          {children}
          <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
            <button
              type="button"
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
              onClick={() => {
                onClear();
                setOpen(false);
              }}>
              Clear
            </button>
            <button
              type="button"
              className="rounded-full bg-[#2678FA] px-5 py-1.5 text-sm font-semibold text-white hover:bg-[#1f66db]"
              onClick={() => {
                onApply();
                setOpen(false);
              }}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPill;
